import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/config";
import { Plus, Edit2, Trash2, X, Save, Upload, GripVertical } from "lucide-react";

const EMPTY = { name: "", logo: "", order: 0, published: true };

function formatErr(d) {
  if (!d) return "Erreur";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/clients").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const onEdit = (c) => { setEditing(c.id); setForm({ name: c.name, logo: c.logo, order: c.order, published: c.published }); setErr(""); };
  const onNew = () => { setEditing("new"); setForm({ ...EMPTY, order: items.length + 1 }); setErr(""); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = data.url.startsWith("http") ? data.url : `${API.replace(/\/api$/, "")}${data.url}`;
      setForm(f => ({ ...f, logo: fullUrl }));
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSave = async () => {
    setErr("");
    try {
      if (editing === "new") await api.post("/admin/clients", form);
      else await api.put(`/admin/clients/${editing}`, form);
      setEditing(null); load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ce logo client ?")) return;
    await api.delete(`/admin/clients/${id}`); load();
  };

  const move = async (c, delta) => {
    await api.put(`/admin/clients/${c.id}`, { order: c.order + delta });
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <p className="section-num">Logos clients</p>
          <h1 className="display text-4xl mt-3">Marquee des clients</h1>
          <p className="text-ink/60 mt-2 text-sm">Les logos seront affichés en blanc sur fond indigo. Préférez les PNG transparents.</p>
        </div>
        <button onClick={onNew} className="btn-pill btn-primary" data-testid="admin-new-client">
          <Plus size={16}/> Ajouter un client
        </button>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(c => (
          <div key={c.id} className="bg-paper border border-ink/10 p-6" data-testid={`admin-client-${c.id}`}>
            <div className="h-20 bg-brand-deep rounded flex items-center justify-center p-4">
              {c.logo
                ? <img src={c.logo} alt={c.name} className="max-h-full max-w-full object-contain opacity-90"/>
                : <span className="text-white/60 text-sm italic">Aucun logo</span>}
            </div>
            <div className="flex items-center justify-between mt-4">
              <div>
                <p className="display text-lg leading-tight">{c.name}</p>
                <p className="text-[12px] text-ink/60">Ordre {c.order} · {c.published ? "Publié" : "Caché"}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => move(c, -1)} className="p-2 hover:bg-ink/5" title="Monter">↑</button>
                <button onClick={() => move(c, 1)} className="p-2 hover:bg-ink/5" title="Descendre">↓</button>
                <button onClick={() => onEdit(c)} className="p-2 hover:bg-ink/5"><Edit2 size={14}/></button>
                <button onClick={() => onDelete(c.id)} className="p-2 hover:bg-paper-2 text-accent-pink"><Trash2 size={14}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="bg-paper max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-8 border-b border-ink/10 flex items-center justify-between">
              <h2 className="display text-2xl">{editing === "new" ? "Nouveau client" : "Modifier le logo"}</h2>
              <button onClick={() => setEditing(null)}><X size={22}/></button>
            </div>
            <div className="p-8 space-y-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Nom de l'entreprise</label>
                <input className="field" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="ex : Airbus"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Logo</label>
                {form.logo && (
                  <div className="mt-3 bg-brand-deep p-6 rounded inline-block">
                    <img src={form.logo} alt="" className="max-h-20 max-w-xs opacity-90"/>
                  </div>
                )}
                <div className="flex gap-3 mt-3 items-center">
                  <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden"/>
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-pill btn-ghost disabled:opacity-60">
                    {uploading ? "Envoi…" : (<><Upload size={14}/> {form.logo ? "Remplacer le logo" : "Uploader un logo"}</>)}
                  </button>
                </div>
                <p className="text-[11px] text-ink/50 mt-3">PNG transparent recommandé, hauteur 200-400px. Sera affiché en blanc sur fond indigo.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-wider text-ink/60">Ordre d'affichage</label>
                  <input type="number" className="field" value={form.order} onChange={e => setForm(f => ({...f, order: parseInt(e.target.value) || 0}))}/>
                </div>
                <label className="flex items-center gap-3 pt-7">
                  <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({...f, published: e.target.checked}))}/>
                  <span className="text-sm">Visible dans le marquee</span>
                </label>
              </div>
              {err && <p className="text-accent-pink text-sm">{err}</p>}
              <div className="flex gap-3 pt-4">
                <button onClick={onSave} className="btn-pill btn-primary"><Save size={16}/> Enregistrer</button>
                <button onClick={() => setEditing(null)} className="btn-pill btn-ghost">Annuler</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
